import type {
  GuardianCapabilityName,
  GuardianLinkStatus,
  GuardianRelationship,
} from '@stm/contracts';
import { guardianCapabilityNames, guardianRelationships } from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const guardianLinkStatusKeys: Readonly<Record<GuardianLinkStatus, TranslationKey>> = {
  pending: 'guardianLinks.status.pending',
  approved: 'guardianLinks.status.approved',
  rejected: 'guardianLinks.status.rejected',
  revoked: 'guardianLinks.status.revoked',
};

export const guardianRelationshipKeys: Readonly<Record<GuardianRelationship, TranslationKey>> = {
  parent: 'guardianLinks.relationship.parent',
  legal_guardian: 'guardianLinks.relationship.legal_guardian',
  grandparent: 'guardianLinks.relationship.grandparent',
  other: 'guardianLinks.relationship.other',
};

export const guardianCapabilityKeys: Readonly<Record<GuardianCapabilityName, TranslationKey>> = {
  viewSchedule: 'guardianLinks.capability.viewSchedule',
  respondRsvp: 'guardianLinks.capability.respondRsvp',
  viewPublishedResults: 'guardianLinks.capability.viewPublishedResults',
  viewMembership: 'guardianLinks.capability.viewMembership',
  receiveNotices: 'guardianLinks.capability.receiveNotices',
};

/** The list filter: one status or everything. Waiting links come first because they need a decision. */
export type GuardianLinkFilter = GuardianLinkStatus | 'all';
export const guardianLinkFilters: readonly GuardianLinkFilter[] = [
  'pending',
  'approved',
  'rejected',
  'revoked',
  'all',
];

export const guardianLinkFilterKeys: Readonly<Record<GuardianLinkFilter, TranslationKey>> = {
  ...guardianLinkStatusKeys,
  all: 'guardianLinks.filter.all',
};

export function guardianLinkListPath(
  base: string,
  options: { status: GuardianLinkFilter; cursor?: string | null },
): string {
  const query = new URLSearchParams();
  if (options.status !== 'all') query.set('status', options.status);
  if (options.cursor !== undefined && options.cursor !== null) query.set('cursor', options.cursor);
  const text = query.toString();
  return `${base}/guardian-links${text === '' ? '' : `?${text}`}`;
}

export type GuardianLinkDecisionName = 'approve' | 'reject' | 'revoke';

/** What can be decided: a waiting link is approved or rejected, an approved one revoked. */
export function availableDecisions(status: GuardianLinkStatus): GuardianLinkDecisionName[] {
  if (status === 'pending') return ['approve', 'reject'];
  if (status === 'approved') return ['revoke'];
  return [];
}

/** An athlete chosen for the link request, with the label shown in the form. */
export interface ChosenAthlete {
  id: string;
  label: string;
}

export interface GuardianLinkForm {
  /** One link is requested per athlete, so a guardian is tied to several children at once. */
  athletes: ChosenAthlete[];
  guardianSource: 'existing' | 'new';
  guardianPersonId: string;
  /** The chosen guardian's name, shown while the picker itself is empty. */
  guardianLabel: string;
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  relationship: GuardianRelationship;
  validFrom: string;
  validUntil: string;
  capabilities: Record<GuardianCapabilityName, boolean>;
  /** Approve each link right after requesting it, once the relationship has been verified. */
  approveNow: boolean;
}

/** A new request offers every capability, so the secretary removes what the family should not have. */
export function emptyGuardianLinkForm(): GuardianLinkForm {
  return {
    athletes: [],
    guardianSource: 'existing',
    guardianPersonId: '',
    guardianLabel: '',
    givenName: '',
    familyName: '',
    email: '',
    phone: '',
    relationship: 'parent',
    validFrom: '',
    validUntil: '',
    capabilities: Object.fromEntries(guardianCapabilityNames.map((name) => [name, true])) as Record<
      GuardianCapabilityName,
      boolean
    >,
    approveNow: false,
  };
}

/** Adds a chosen athlete once; choosing the same athlete again changes nothing. */
export function withAthlete(form: GuardianLinkForm, athlete: ChosenAthlete): GuardianLinkForm {
  if (athlete.id === '' || form.athletes.some((chosen) => chosen.id === athlete.id)) return form;
  return { ...form, athletes: [...form.athletes, athlete] };
}

export function withoutAthlete(form: GuardianLinkForm, athleteId: string): GuardianLinkForm {
  return { ...form, athletes: form.athletes.filter((chosen) => chosen.id !== athleteId) };
}

export function canSubmitGuardianLink(form: GuardianLinkForm): boolean {
  if (form.athletes.length === 0) return false;
  if (!guardianRelationships.includes(form.relationship)) return false;
  return form.guardianSource === 'existing'
    ? form.guardianPersonId !== ''
    : form.givenName.trim() !== '' && form.familyName.trim() !== '';
}

/**
 * The request body for one athlete: the guardian is either an existing person or new details, never
 * both. `createdGuardianPersonId` is the person the first request of a batch created, so the next
 * athletes are linked to that same guardian instead of creating another one.
 */
export function guardianLinkRequestBody(
  form: GuardianLinkForm,
  athleteId: string,
  createdGuardianPersonId: string | null = null,
): Record<string, unknown> {
  const guardian =
    createdGuardianPersonId !== null
      ? { guardianPersonId: createdGuardianPersonId }
      : form.guardianSource === 'existing'
        ? { guardianPersonId: form.guardianPersonId }
        : {
            newGuardian: {
              givenName: form.givenName,
              familyName: form.familyName,
              email: form.email,
              phone: form.phone,
            },
          };
  return {
    athleteId,
    ...guardian,
    relationship: form.relationship,
    ...(form.validFrom === '' ? {} : { validFrom: form.validFrom }),
    validUntil: form.validUntil === '' ? null : form.validUntil,
    capabilities: form.capabilities,
  };
}
